import { useCallback } from 'react';
import { usePedigreeContext } from '../context/usePedigreeContext';
import BaseGraph from '../model';
import { PedigreeCondition } from '../types';
import { isNil } from 'lodash';

// -----------------------------------------------------------------------------
// these are definitions for easier reading and having less lines of code due to
// repetition and formatting
const PERSON = BaseGraph.TYPE.PERSON;
const REL = BaseGraph.TYPE.RELATIONSHIP;
const HUB = BaseGraph.TYPE.CHILDHUB;

// -----------------------------------------------------------------------------
export const useGraphData = () => {
  const {
    extraData,
    setExtraData,
    baseGraph,
    setRefreshFlag,
    dynamicGraph,
    setBaseGraph,
    saveStateForUndo,
  } = usePedigreeContext();

  // ---------------------------------------------------------------------------
  const getNodeType = (
    nodeId: number
  ): 'person' | 'relationship' | 'childhub' | null => {
    if (!baseGraph) {
      return null;
    }

    const type = baseGraph.type[nodeId];
    switch (type) {
      case PERSON:
        return 'person';
      case REL:
        return 'relationship';
      case HUB:
        return 'childhub';
      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  const getNodeData = (nodeId: number | undefined | null) => {
    if (!baseGraph) {
      return null;
    }

    if (isNil(nodeId)) {
      return null;
    }

    return baseGraph.properties[nodeId];
  };

  // ---------------------------------------------------------------------------
  const editNodeData = (
    nodeId: number | undefined | null,
    data: any,
    refresh = true
  ) => {
    if (!baseGraph) {
      return false;
    }

    if (isNil(nodeId)) {
      return false;
    }

    saveStateForUndo();
    try {
      baseGraph.properties[nodeId] = {
        ...baseGraph.properties[nodeId],
        ...data,
      };

      if (refresh) {
        setRefreshFlag(true);
      }

      return true;
    } catch (error) {
      console.error('Error editing node:', error);
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  const addChild = (nodeId: number | undefined) => {
    if (!baseGraph) {
      return;
    }

    if (nodeId === undefined) {
      return;
    }

    saveStateForUndo();
    const relationships = baseGraph.getAllRelationships(nodeId);
    if (relationships.length === 0) {
      return;
    }

    let childHubNodeId = baseGraph.getRelationshipChildhub(relationships[0]);
    if (childHubNodeId === null || childHubNodeId === undefined) {
      childHubNodeId = baseGraph.insertVertex(HUB, {}, 1, [nodeId], []);
    }

    baseGraph.insertVertex(PERSON, {}, 1, [childHubNodeId], []);
    setRefreshFlag(true);
  };

  // ---------------------------------------------------------------------------
  const addParents = (nodeId: number | undefined) => {
    if (nodeId === undefined || !dynamicGraph || !baseGraph) {
      return;
    }

    if (!baseGraph?.isPerson(nodeId)) {
      return;
    }

    saveStateForUndo();
    dynamicGraph.addNewParents(nodeId);
    const updatedBaseGraph = dynamicGraph.DG.GG;
    const parents = updatedBaseGraph.getParents(nodeId);

    setBaseGraph(updatedBaseGraph);
    setRefreshFlag(true);
    return parents;
  };

  // ---------------------------------------------------------------------------
  const addPartner = (nodeId: number | undefined) => {
    if (nodeId === undefined) {
      return;
    }

    if (!baseGraph?.isPerson(nodeId)) {
      return;
    }

    saveStateForUndo();
    const gender = getNodeData(nodeId)?.gender === 'M' ? 'F' : 'M';
    const partnerId = baseGraph.insertVertex(PERSON, { gender }, 1, [], []);
    const relId = baseGraph.insertVertex(REL, {}, 1, [nodeId, partnerId], []);
    const childHubId = baseGraph.insertVertex(HUB, {}, 1, [relId], []);
    baseGraph.insertVertex(PERSON, { gender: 'U' }, 1, [childHubId], []);

    setRefreshFlag(true);
  };

  // ---------------------------------------------------------------------------
  const addRelationship = (
    nodeIdA: number | undefined,
    nodeIdB: number | undefined
  ) => {
    if (nodeIdA === undefined || nodeIdB === undefined) {
      return;
    }

    if (nodeIdA === nodeIdB) {
      return;
    }

    if (!baseGraph?.isPerson(nodeIdA) || !baseGraph?.isPerson(nodeIdB)) {
      return;
    }

    const partners = baseGraph.getAllPartners(nodeIdA);
    if (partners.includes(nodeIdB)) {
      return;
    }

    saveStateForUndo();
    const relId = baseGraph.insertVertex(REL, {}, 1, [nodeIdA, nodeIdB], []);
    const childHubId = baseGraph.insertVertex(HUB, {}, 1, [relId], []);
    baseGraph.insertVertex(PERSON, { gender: 'U' }, 1, [childHubId], []);

    setRefreshFlag(true);
  };

  // ---------------------------------------------------------------------------
  const addSibling = (nodeId: number | undefined) => {
    if (nodeId === undefined) {
      return;
    }

    if (!baseGraph?.isPerson(nodeId)) {
      return;
    }

    saveStateForUndo();
    let parents = baseGraph.getParents(nodeId);
    if (parents.length < 2) {
      // add parents first
      parents = addParents(nodeId);
      if (!parents) {
        return;
      }
    }

    // first we need to find the relationship node between the parents, then its childhub
    try {
      const relId = baseGraph.getRelationshipNode(parents[0], parents[1]);
      const childHubNodeId = baseGraph.getRelationshipChildhub(relId);
      if (childHubNodeId === null || childHubNodeId === undefined) {
        return;
      }

      baseGraph.insertVertex(PERSON, {}, 1, [childHubNodeId], []);
      setRefreshFlag(true);
    } catch (error) {
      console.error('Error adding sibling:', error);
    }
  };

  // ---------------------------------------------------------------------------
  const addTwin = (nodeId: number | undefined) => {
    if (nodeId === undefined || !dynamicGraph || !baseGraph) {
      return;
    }

    if (!baseGraph?.isPerson(nodeId)) {
      return;
    }

    saveStateForUndo();
    let parents = baseGraph.getParents(nodeId);
    if (parents.length < 2) {
      // add parents first
      parents = addParents(nodeId);
      if (!parents) {
        return;
      }
    }

    try {
      const nodeData = getNodeData(nodeId);
      const twinProperties = {
        gender: nodeData?.gender || 'U',
      };

      dynamicGraph.addTwin(nodeId, twinProperties);
      const updatedBaseGraph = dynamicGraph.DG.GG;

      setBaseGraph(updatedBaseGraph);
      setRefreshFlag(true);
      return true;
    } catch (error) {
      console.error('Error adding twin:', error);
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  const hasParents = (nodeId: number) => {
    if (!baseGraph) {
      return false;
    }

    if (isNil(nodeId)) {
      return false;
    }

    const parents = baseGraph.getParents(nodeId);
    return parents.length === 2;
  };

  // ---------------------------------------------------------------------------
  const hasPartner = (nodeId: number) => {
    if (!baseGraph) {
      return false;
    }

    if (isNil(nodeId)) {
      return false;
    }

    const partners = baseGraph.getAllPartners(nodeId);
    return partners.length > 0;
  };

  // ---------------------------------------------------------------------------
  const deleteNode = (nodeId: number | undefined) => {
    if (nodeId === undefined || !baseGraph || !dynamicGraph) {
      console.warn(
        'Cannot delete node: invalid nodeId or graph is not available'
      );
      return false;
    }

    try {
      // Check if node exists
      if (!baseGraph.properties[nodeId]) {
        console.warn(`Node ${nodeId} does not exist`);
        return false;
      }

      const nodeData = baseGraph.properties[nodeId];

      // Check if this is the proband - might want to prevent deletion
      if (nodeData.isProband) {
        console.warn('Cannot delete proband node');
        return false;
      }

      saveStateForUndo();

      const disconnectedList =
        dynamicGraph.getDisconnectedSetIfNodeRemoved(nodeId);

      dynamicGraph.removeNodes(disconnectedList || []);
      const bg = dynamicGraph.DG.GG;

      // remove all single person twin groups
      const nodes = bg.getNumVertices();
      for (let i = 0; i < nodes; i++) {
        if (!bg.isPerson(i)) {
          continue;
        }

        const twinGroup = bg.getTwinGroupId(i);
        if (isNil(twinGroup)) {
          continue;
        }

        try {
          const twins = bg.getAllTwinsOf(i);
          if (twins.length <= 1) {
            delete bg.properties[i]['twinGroup'];
          }
        } catch {
          delete bg.properties[i]['twinGroup'];
        }
      }

      setBaseGraph(bg);
      setRefreshFlag(true);
      return true;
    } catch (error) {
      console.error('Error deleting node:', error);
      return false;
    }
  };

  //----------------------------------------------------------------------------
  const handleAddCondition = useCallback(
    (newCondition: Omit<PedigreeCondition, 'id'>): PedigreeCondition => {
      saveStateForUndo();
      const condition: PedigreeCondition = {
        ...newCondition,
        id: Date.now().toString(),
      };

      setExtraData({
        ...extraData,
        conditions: [...extraData.conditions, condition],
      });

      return condition;
    },
    [extraData, saveStateForUndo, setExtraData]
  );

  //----------------------------------------------------------------------------
  const handleRemoveCondition = useCallback(
    (conditionId: string) => {
      saveStateForUndo();

      const updatedConditions = extraData.conditions.filter(
        (c) => c.id !== conditionId
      );

      setExtraData({
        ...extraData,
        conditions: updatedConditions,
      });

      if (!baseGraph) {
        return;
      }
      Object.keys(baseGraph.properties).forEach((nodeKey) => {
        const nodeId = parseInt(nodeKey, 10);
        const node = baseGraph.properties[nodeId];

        if (baseGraph.type[nodeId] !== PERSON) {
          return;
        }

        baseGraph.properties[nodeId] = {
          ...node,
          conditions: (node.conditions || []).filter(
            (condition: any) => condition.id !== conditionId
          ),
        };
      });

      setRefreshFlag(true);
    },
    [baseGraph, extraData, saveStateForUndo, setExtraData, setRefreshFlag]
  );
  // ---------------------------------------------------------------------------
  return {
    conditions: extraData.conditions || [],
    handleAddCondition,
    handleRemoveCondition,
    getNodeType,
    getNodeData,
    editNodeData,
    addChild,
    addParents,
    addPartner,
    addRelationship,
    addSibling,
    addTwin,
    hasParents,
    hasPartner,
    deleteNode,
  };
};
